import { defineStore } from "pinia";
// Type imports
import type {
	Application,
	Currency
} from "@nwt/norce-pwa/dist/runtime/types/api/application/application";
import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { SbBlokData } from "@storyblok/js";
import type {
	Account,
	MenuLink,
	ResolvedMenuNode,
	Search,
	SiteLogo,
	TopNavbarLeft,
	ApplicationFlag,
	StockStatusItem
} from "~/types/storyblok/storyblok";
import type { CulturesNorce } from "~/types/components/header";
import type { ExtractedFooterFields } from "~/types/components/footer";

interface SiteLogoDimensions {
	width: number;
	height: number;
}

interface State {
	siteLogo: SiteLogo | undefined;
	siteLogoWhite: SiteLogo | undefined;
	siteLogoDimensions: SiteLogoDimensions;
	search: Search[];
	account: Account[];
	topNavbarLeft: TopNavbarLeft[];
	/** Storyblok `MenuNode` menu from /api/menu; empty ⇒ the legacy fields below. */
	menu: ResolvedMenuNode[];
	menuCategories: MenuLink[];
	menuCategoriesNorce: Category[];
	applicationNorce: Application | null;
	culturesNorce: CulturesNorce[];
	currenciesNorce: Currency[];
	headerLoaded: boolean;
	flags: ApplicationFlag[];
	topBar: SbBlokData[];
	lipscore_api_key: string | null;
	lipscore_enable: boolean | null;
	stockStatus: StockStatusItem[];
	footer: ExtractedFooterFields | null;
}

export const useAppStore = defineStore("appStore", {
	state: (): State => ({
		siteLogo: undefined,
		siteLogoWhite: undefined,
		siteLogoDimensions: {
			width: 160,
			height: 52
		},
		search: [],
		account: [],
		topNavbarLeft: [],
		menu: [],
		menuCategories: [],
		menuCategoriesNorce: [],
		applicationNorce: null,
		culturesNorce: [],
		currenciesNorce: [],
		headerLoaded: false,
		flags: [],
		topBar: [],
		lipscore_api_key: null,
		lipscore_enable: null,
		stockStatus: [],
		footer: null
	}),

	getters: {
		getAppStore: (state) => state
	},

	actions: {
		initState(data: Partial<State>) {
			this.$patch((state) => {
				if (data.siteLogo) state.siteLogo = data.siteLogo;
				if (data.siteLogoWhite) state.siteLogoWhite = data.siteLogoWhite;
				if (data.search) state.search = data.search;
				if (data.account) state.account = data.account;
				if (data.topNavbarLeft) state.topNavbarLeft = data.topNavbarLeft;
				if (data.menu) state.menu = data.menu;
				if (data.menuCategories) state.menuCategories = data.menuCategories;
				if (data.menuCategoriesNorce)
					state.menuCategoriesNorce = data.menuCategoriesNorce;
				if (data.applicationNorce)
					state.applicationNorce = data.applicationNorce;
				if (data.culturesNorce) state.culturesNorce = data.culturesNorce;
				if (data.currenciesNorce) state.currenciesNorce = data.currenciesNorce;
				if (data.headerLoaded) state.headerLoaded = data.headerLoaded;
				if (data.flags) state.flags = data.flags;
				if (data.topBar) state.topBar = data.topBar;
				if (data.lipscore_api_key !== undefined)
					state.lipscore_api_key = data.lipscore_api_key;
				if (data.lipscore_enable !== undefined)
					state.lipscore_enable = data.lipscore_enable;
				if (data.stockStatus) state.stockStatus = data.stockStatus;
				if (data.footer) state.footer = data.footer;
			});
		}
	}
});
